import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import './App.css';
import { AuthProvider, useAuth } from './components/AuthContext';
import Login from './pages/Login';
import WorkspaceSelector from './pages/WorkspaceSelector';
import Landing from './pages/Landing';
import History from './pages/History';
import OutputViewer from './pages/OutputViewer';
import ValidationResults from './pages/ValidationResults';
import ConfigEditor from './pages/ConfigEditor';
import Pipeline from './pages/Pipeline';
import AdminConsole from './pages/AdminConsole';
import Settings from './pages/Settings';

/* Map old page keys → URL paths */
const PAGE_PATHS = {
  landing:    '/',
  pipeline:   '/pipeline',
  config:     '/workspace/design',
  history:    '/history',
  outputs:    '/outputs',
  validation: '/validation',
  admin:      '/admin',
  settings:   '/settings',
};

/* Reverse: path → page key */
const pathToPage = (pathname) => {
  for (const [key, path] of Object.entries(PAGE_PATHS)) {
    if (pathname === path) return key;
  }
  if (pathname === '/pipeline') return 'pipeline';
  if (pathname === '/history') return 'history';
  if (pathname === '/admin') return 'admin';
  if (pathname.startsWith('/workspace/design')) return 'config';
  if (pathname === '/settings') return 'settings';
  return 'landing';
};

/* ── Initials helper ── */
const getInitials = (name) => {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
};

function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, workspace, logout, makeAdmin, updateProfile } = useAuth();
  const currentPage = pathToPage(location.pathname);

  const [jobId, setJobId] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editAvatarUrl, setEditAvatarUrl] = useState('');
  const [editProfileLoading, setEditProfileLoading] = useState(false);
  const [editProfileError, setEditProfileError] = useState('');
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('synthloom_dark_mode') === 'true';
  });

  // Apply dark class on <html>
  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
    localStorage.setItem('synthloom_dark_mode', darkMode);
  }, [darkMode]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileOpen && !e.target.closest('.profile-menu-wrapper')) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileOpen]);

  const navigateTo = (page) => {
    const path = PAGE_PATHS[page] || '/';
    guardedNavigate(path);
  };

  // Guard navigation: if ConfigEditor has unsaved changes, prompt before leaving
  const guardedNavigate = useCallback(async (path) => {
    if (location.pathname === '/workspace/design' && path !== '/workspace/design' && window.__configEditorPromptSave) {
      const result = await window.__configEditorPromptSave();
      if (result === 'cancel') return;
      if (result === 'save') {
        if (window.__configEditorSave) {
          await window.__configEditorSave();
        }
      }
      // 'discard' or 'save' — both proceed
    }
    navigate(path);
  }, [navigate, location.pathname]);

  const openEditProfile = () => {
    setEditDisplayName(user?.display_name || user?.name || '');
    setEditAvatarUrl(user?.avatar_url || '');
    setEditProfileError('');
    setEditProfileOpen(true);
    setProfileOpen(false);
  };

  const saveProfile = async () => {
    setEditProfileLoading(true);
    setEditProfileError('');
    try {
      const body = {};
      const newName = editDisplayName.trim();
      if (newName && newName !== (user?.display_name || user?.name)) body.display_name = newName;
      const newAvatar = editAvatarUrl.trim();
      if (newAvatar !== (user?.avatar_url || '')) body.avatar_url = newAvatar;
      if (Object.keys(body).length === 0) { setEditProfileOpen(false); return; }
      await updateProfile(body);
      setEditProfileOpen(false);
    } catch (err) {
      setEditProfileError(err.message);
    } finally {
      setEditProfileLoading(false);
    }
  };

  /* ── Auth guard: unauthenticated → login ── */
  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="*" element={<Login />} />
      </Routes>
    );
  }

  /* ── Authenticated but no workspace selected → workspace selector ── */
  if (!workspace) {
    return (
      <Routes>
        <Route path="*" element={<WorkspaceSelector />} />
      </Routes>
    );
  }

  const initials = getInitials(user?.display_name || user?.name);
  const avatarUrl = user?.avatar_url;

  return (
    <div className="app">
      {/* Top Navbar - always visible */}
      <header className="top-navbar">
        <div className="navbar-left">
          <div className="navbar-brand" onClick={() => guardedNavigate('/')}>
            <span className="brand-icon">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <rect y="1" width="16" height="2.4" rx="1.2" fill="#818cf8"/>
                <rect y="6.8" width="12" height="2.4" rx="1.2" fill="#6366f1"/>
                <rect y="12.6" width="8" height="2.4" rx="1.2" fill="#4f46e5"/>
              </svg>
            </span>
            <span className="brand-name">Synth<em className="brand-accent">loom</em></span>
          </div>
          {/* Workspace badge */}
          <button className="workspace-badge" onClick={() => guardedNavigate('/workspaces')} title="Switch workspace">
            <svg width="12" height="12" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round">
              <rect x="1.5" y="1.5" width="4.5" height="4.5" rx="1" />
              <rect x="8" y="1.5" width="4.5" height="4.5" rx="1" />
              <rect x="1.5" y="8" width="4.5" height="4.5" rx="1" />
              <rect x="8" y="8" width="4.5" height="4.5" rx="1" />
            </svg>
            {workspace.name}
          </button>
        </div>

        <nav className="navbar-center">
          <button
            className={`navbar-link ${currentPage === 'landing' ? 'active' : ''}`}
            onClick={() => guardedNavigate('/')}
          >
            Home
          </button>
          <button
            className={`navbar-link ${currentPage === 'pipeline' ? 'active' : ''}`}
            onClick={() => guardedNavigate('/pipeline')}
          >
            Pipeline
          </button>
          <button
            className={`navbar-link ${currentPage === 'config' ? 'active' : ''}`}
            onClick={() => guardedNavigate('/workspace/design')}
          >
            Design Studio
          </button>
          <button
            className={`navbar-link ${currentPage === 'history' ? 'active' : ''}`}
            onClick={() => guardedNavigate('/history')}
          >
            History
          </button>
          {user?.is_admin && (
            <button
              className={`navbar-link ${currentPage === 'admin' ? 'active' : ''}`}
              onClick={() => guardedNavigate('/admin')}
            >
              Admin
            </button>
          )}
          <button
            className={`navbar-link ${currentPage === 'settings' ? 'active' : ''}`}
            onClick={() => guardedNavigate('/settings')}
          >
            Settings
          </button>
        </nav>

        <div className="navbar-right">
          <div className="profile-menu-wrapper">
            <div
              className={`user-avatar ${profileOpen ? 'active' : ''}`}
              onClick={() => setProfileOpen(!profileOpen)}
            >
              {avatarUrl
                ? <img src={avatarUrl} alt="" className="user-avatar-img" />
                : initials}
            </div>

            {profileOpen && (
              <div className="profile-dropdown">

                <div className="profile-dropdown-header">
                  <div className="profile-avatar-lg">
                    {avatarUrl
                      ? <img src={avatarUrl} alt="" className="profile-avatar-img" />
                      : initials}
                  </div>
                  <div className="profile-info">
                    <span className="profile-name">{user?.display_name || user?.name || 'User'}</span>
                    <span className="profile-email">{user?.email || ''}</span>
                    {user?.plan && (
                      <span className="profile-plan" style={{ fontSize: 12, color: '#6366f1', fontWeight: 500 }}>
                        {user.plan.charAt(0).toUpperCase() + user.plan.slice(1)} plan
                      </span>
                    )}
                  </div>
                </div>

                <div className="profile-dropdown-divider" />

                <button className="profile-dropdown-item" onClick={openEditProfile}>
                  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 3l5 5-9.5 9.5H3V13z" /><line x1="10" y1="5" x2="15" y2="10" />
                  </svg>
                  Edit Profile
                </button>

                <div className="profile-dropdown-row">
                  <span className="profile-row-label">Workspace</span>
                  <span className="profile-row-value" style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{workspace.name}</span>
                </div>

                <button className="profile-dropdown-item" onClick={() => { setProfileOpen(false); guardedNavigate('/workspaces'); }}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round">
                    <rect x="1.5" y="1.5" width="4.5" height="4.5" rx="1" />
                    <rect x="8" y="1.5" width="4.5" height="4.5" rx="1" />
                    <rect x="1.5" y="8" width="4.5" height="4.5" rx="1" />
                    <rect x="8" y="8" width="4.5" height="4.5" rx="1" />
                  </svg>
                  Switch workspace
                </button>

                {user?.is_admin && (
                  <button className="profile-dropdown-item" onClick={() => { setProfileOpen(false); guardedNavigate('/admin'); }}>
                    <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                      <circle cx="10" cy="10" r="3" /><path d="M10 2v2m0 12v2m-5.66-3.34 1.42-1.42m8.48-8.48 1.42-1.42M2 10h2m12 0h2m-3.34 5.66-1.42-1.42M5.76 5.76 4.34 4.34" />
                    </svg>
                    Admin Console
                  </button>
                )}

                {/* Only show Enable Admin if not admin and not the first user */}
                {!user?.is_admin && user?.id !== 1 && (
                  <button className="profile-dropdown-item" onClick={async () => {
                    try { await makeAdmin(); setProfileOpen(false); guardedNavigate('/admin'); }
                    catch (err) { alert(err.message); }
                  }}>
                    <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                      <path d="M12 2l2 4h4l-3 3 1 5-4-2-4 2 1-5-3-3h4z" />
                    </svg>
                    Enable Admin
                  </button>
                )}

                <div className="profile-dropdown-divider" />

                <div className="profile-dropdown-row">
                  <span className="profile-row-label">Dark mode</span>
                  <button
                    className={`theme-toggle ${darkMode ? 'on' : ''}`}
                    onClick={() => setDarkMode(!darkMode)}
                    aria-label="Toggle dark mode"
                  >
                    <span className="theme-toggle-thumb" />
                  </button>
                </div>

                <div className="profile-dropdown-divider" />

                <button className="profile-dropdown-item" onClick={() => { setProfileOpen(false); logout(); }}>
                  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M7 17H4a2 2 0 01-2-2V5a2 2 0 012-2h3" />
                    <polyline points="11 15 15 10 11 5" />
                    <line x1="15" y1="10" x2="5" y2="10" />
                  </svg>
                  Log out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="app-main">
        <Routes>
          <Route path="/" element={<Landing onNavigate={navigateTo} />} />
          <Route path="/workspaces" element={<WorkspaceSelector />} />
          <Route path="/history" element={<History />} />
          <Route path="/admin" element={<AdminConsole />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/pipeline" element={
            <Pipeline onNavigate={navigateTo} />
          } />
          <Route path="/workspace/design" element={
            <ConfigEditor onNavigate={() => navigate('/')} />
          } />
          <Route path="/outputs" element={
            <OutputViewer jobId={jobId} onNavigate={navigateTo} />
          } />
          <Route path="/validation" element={
            jobId ? <ValidationResults jobId={jobId} /> : <Navigate to="/" replace />
          } />
          {/* Catch-all → home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      {/* ═══ Edit Profile Modal ═══ */}
      {editProfileOpen && (
        <div className="edit-profile-overlay" onClick={() => setEditProfileOpen(false)}>
          <div className="edit-profile-modal" onClick={(e) => e.stopPropagation()}>
            <div className="edit-profile-header">
              <h3>Edit Profile</h3>
              <button className="edit-profile-close" onClick={() => setEditProfileOpen(false)}>
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                  <line x1="5" y1="5" x2="15" y2="15"/><line x1="15" y1="5" x2="5" y2="15"/>
                </svg>
              </button>
            </div>

            <div className="edit-profile-body">
              {/* Avatar preview */}
              <div className="edit-profile-avatar-section">
                <div className="edit-profile-avatar-preview">
                  {editAvatarUrl
                    ? <img src={editAvatarUrl} alt="" className="profile-avatar-img" onError={(e) => { e.target.style.display = 'none'; }} />
                    : <span className="edit-profile-avatar-initials">{getInitials(editDisplayName || user?.name)}</span>}
                </div>
                <span className="edit-profile-avatar-hint">Profile Photo</span>
              </div>

              {/* Display Name */}
              <div className="edit-profile-field">
                <label>DISPLAY NAME</label>
                <input
                  type="text"
                  value={editDisplayName}
                  onChange={(e) => setEditDisplayName(e.target.value)}
                  placeholder="Your display name"
                />
              </div>

              {/* Avatar URL */}
              <div className="edit-profile-field">
                <label>AVATAR URL</label>
                <input
                  type="url"
                  value={editAvatarUrl}
                  onChange={(e) => setEditAvatarUrl(e.target.value)}
                  placeholder="https://example.com/photo.jpg"
                />
                <span className="edit-profile-field-hint">Paste a link to your profile photo. Supports any public image URL.</span>
              </div>

              {/* Username + email (read-only) */}
              <div className="edit-profile-field">
                <label>USERNAME</label>
                <input type="text" value={user?.username || '—'} disabled />
              </div>
              <div className="edit-profile-field">
                <label>EMAIL</label>
                <input type="email" value={user?.email || ''} disabled />
              </div>

              {editProfileError && <div className="edit-profile-error">{editProfileError}</div>}
            </div>

            <div className="edit-profile-footer">
              <button className="edit-profile-cancel" onClick={() => setEditProfileOpen(false)}>Cancel</button>
              <button className="edit-profile-save" onClick={saveProfile} disabled={editProfileLoading}>
                {editProfileLoading ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppShell />
    </AuthProvider>
  );
}

export default App;
